import React, { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Grid,
  InputLabel,
  makeStyles,
  TextField,
  CircularProgress,
  useTheme, 
  useMediaQuery
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { green } from '@material-ui/core/colors';
import { STATUS_API } from 'src/app/constant/config';
import * as Yup from 'yup';
import { Formik } from 'formik';
import { createServicePackage } from 'src/features/servicePackageSlice';
import CustomDialog from 'src/app/components/CustomDialog';

const initStateFromValue = {
  device_type_id: '',
  version: '',
  cost: '',
  day_limit: '',
  name: '',
  description: ''
};

const CreateServicePackage = ({ open, onClose }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const statusCreate = useSelector(
    state => state.servicePackageSlice.statusCreate
  );
  const listDeviceType = useSelector(
    state => state.deviceTypeSlice.listDeviceType
  );
  const deviceType = useMemo(
    () => [
      ...listDeviceType.map(type => ({
        id: type.id,
        name: type.name
      }))
    ],
    [listDeviceType]
  );
  const [initValue, setInitValue] = useState(initStateFromValue);

  const handleSubmit = data => {
    setInitValue(data);
    dispatch(createServicePackage(data));
    onClose();
  };

  const handleClose = () => {
    onClose();
    setInitValue(initStateFromValue);
  };

  const formatNumber = value => {
    if (value == null || value === '') return '';
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  };

  const parseNumber = value => {
    return value.replace(/\./g, '');
  };

  return (
    <CustomDialog
      title="Thêm mới gói dịch vụ"
      open={open}
      onClose={handleClose}
    >
      <div>
        <Formik
          initialValues={initValue}
          validationSchema={Yup.object().shape({
            device_type_id: Yup.string()
              .max(100)
              .required('Tên loại thiết bị không được để trống'),
            name: Yup.string()
              .max(100)
              .required('Tên gói dịch vụ không được để trống'),
            version: Yup.string()
              .max(100)
              .required('Phiên bản không được để trống'),
            cost: Yup.number().required('Giá gói dịch vụ không được để trống'),
            day_limit: Yup.number().required(
              'Hạn gói dịch vụ không được để trống'
            ),
            description: Yup.string()
              .max(100)
              .required('Mô tả không được để trống')
          })}
          onSubmit={handleSubmit}
        >
          {({
            errors,
            handleBlur,
            handleChange,
            handleSubmit,
            setFieldValue,
            touched,
            values
          }) => (
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item md={6} xs={12}>
                  <InputLabel style={{ marginBottom: '21px' }}>
                    Tên loại thiết bị <span className="text-danger">*</span>
                  </InputLabel>
                  <Autocomplete
                    labelId="demo-simple-select-outlined-label"
                    id="demo-simple-select-outlined"
                    size="small"
                    options={deviceType || []}
                    getOptionLabel={option => option.name}
                    value={(deviceType || []).find(
                      option => option.id === values.device_type_id
                    )}
                    onChange={(e, value) =>
                      setFieldValue('device_type_id', Number(value.id))
                    }
                    disableClearable
                    renderInput={params => (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder="Chọn loại thiết bị"
                        name="device_type_id"
                        error={Boolean(
                          touched.device_type_id && errors.device_type_id
                        )}
                        helperText={
                          touched.device_type_id && errors.device_type_id
                        }
                        InputProps={{
                          ...params.InputProps,
                          style: {
                            fontSize: '16px',
                            padding: '0 8px',
                            height: '35px'
                          }
                        }}
                      />
                    )}
                    className={classes.customOutline}
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <InputLabel>
                    Tên gói dịch vụ <span className="text-danger">*</span>
                  </InputLabel>
                  <TextField
                    number
                    error={Boolean(touched.name && errors.name)}
                    className="input-no-fieldset"
                    fullWidth
                    helperText={touched.name && errors.name}
                    margin="normal"
                    name="name"
                    size="small"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.name}
                    variant="outlined"
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <InputLabel>
                    Phiên bản <span className="text-danger">*</span>
                  </InputLabel>
                  <TextField
                    number
                    error={Boolean(touched.version && errors.version)}
                    className="input-no-fieldset"
                    fullWidth
                    helperText={touched.version && errors.version}
                    margin="normal"
                    name="version"
                    size="small"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.version}
                    variant="outlined"
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <InputLabel>
                    Giá gói dịch vụ <span className="text-danger">*</span>
                  </InputLabel>
                  <TextField
                    error={Boolean(touched.cost && errors.cost)}
                    className="input-no-fieldset"
                    fullWidth
                    helperText={touched.cost && errors.cost}
                    margin="normal"
                    name="cost"
                    size="small"
                    onBlur={handleBlur}
                    onChange={e => {
                      const raw = parseNumber(e.target.value);
                      if (!isNaN(raw)) {
                        setFieldValue('cost', raw);
                      }
                    }}
                    value={formatNumber(values.cost)}
                    variant="outlined"
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <InputLabel>
                    Hạn gói dịch vụ (Ngày){' '}
                    <span className="text-danger">*</span>
                  </InputLabel>
                  <TextField
                    error={Boolean(touched.day_limit && errors.day_limit)}
                    className="input-no-fieldset"
                    fullWidth
                    helperText={touched.day_limit && errors.day_limit}
                    margin="normal"
                    name="day_limit"
                    size="small"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.day_limit}
                    variant="outlined"
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <InputLabel>
                    Mô tả <span className="text-danger">*</span>
                  </InputLabel>
                  <TextField
                    error={Boolean(touched.description && errors.description)}
                    className="input-no-fieldset"
                    fullWidth
                    helperText={touched.description && errors.description}
                    margin="normal"
                    name="description"
                    size="small"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.description}
                    variant="outlined"
                  />
                </Grid>
              </Grid>
              <Box my={3}>
                <div className={classes.groupButtonSubmit}>
                  <div className={classes.wrapper}>
                    <Button
                      style={{
                        ...(isMobile && { maxWidth: 90 }),
                        marginRight: '10px',
                        textTranform: 'none !important'
                      }}
                      onClick={() => onClose()}
                      className="btn-main btn-plain mx-3"
                      color="primary"
                      size="large"
                      variant="contained"
                    >
                      Thoát
                    </Button>
                    <Button
                      style={{
                        ...(isMobile && { maxWidth: 120 }),
                        marginRight: '10px',
                        textTranform: 'none !important'
                      }}
                      className="btn-main mx-3"
                      color="primary"
                      size="large"
                      type="submit"
                      variant="contained"
                    >
                      {'Thêm mới'}
                    </Button>
                    {statusCreate === STATUS_API.PENDING && (
                      <CircularProgress
                        size={24}
                        className={classes.buttonProgress}
                      />
                    )}
                  </div>
                </div>
              </Box>
            </form>
          )}
        </Formik>
      </div>
    </CustomDialog>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    minHeight: '100%',
    paddingBottom: theme.spacing(3),
    paddingTop: theme.spacing(3)
  },
  groupButtonSubmit: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: '15px',

    '& .left-button': {
      display: 'flex'
    }
  },
  wrapper: {
    position: 'relative'
  },
  buttonProgress: {
    color: green[500],
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12
  },
  customOutline: {
    '& .MuiOutlinedInput-notchedOutline': {
      borderWidth: '1px',
      borderColor: 'rgba(31, 31, 31, 0.14)',
      borderRadius: '4px'
    }
  }
}));

export default CreateServicePackage;
